/**
 * ContactDetailPageCore
 *
 * Pure presentational component for the contact detail page.
 * Works in both Next.js (with tRPC) and Electron (with IPC) by receiving
 * all data and callbacks via props. Uses render props for platform-specific components.
 */
import type { ComponentType, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import type {
  AiChoice,
  EditorDocument,
  MessageAttachmentState,
  StepChanges,
  StepData,
  StepListItem,
} from '../CadenceStep';
import type { StepResolution } from '../CampaignDayCard/stepResolution';
import type { SiftConfig } from '../CGStep';
import type {
  ActiveSequenceEnrollment,
  OwnerOption,
  StagingCandidate,
  TemplateOption,
} from '../Input2/CampaignButtonWithCadence';
import type { Stage } from '../Input2/StageButton';
import type { LinkedInMapping } from '../LinkedInIdentifiersSection';
import type { PriorSentStepsSummary } from '../Popup/ResumeOrStartFreshPopup';
import type { VoiceRecorderProps } from '../VoiceRecorder';
import type { AddExperienceInput, OrgSearchOption } from './AddExperiencePopup';
import type {
  AIMessageSectionData,
  CadenceGraph,
  StepCreateInput as CadenceStepCreateInput,
  StepUpdateInput as CadenceStepUpdateInput,
  DocumentData,
  TemplateInfo,
} from './CadenceSectionPure';
import type { SwitcherCampaign } from './CampaignSwitcherPure';
import type { ContactEducationItem, ContactExperienceItem } from './ContactEmploymentEducationSectionPure';
import type { ContactImageVariants } from './ContactImagesSectionPure';
import type {
  CadenceData,
  ContactInteractSectionPureProps,
  InteractDisplayMode,
  SequenceViewMode,
} from './ContactInteractSectionPure';
import type {
  ActionSectionUserTeam,
  ActionStep,
  ActionStepConfig,
  PendingStep,
  ReminderCardCallbacks,
  ReminderCardData,
  ScheduleStatus,
  SendMode,
  StepEditorCallbacks,
  StepEditorData,
  StepType,
  VideoNoteCardCallbacks,
  VoiceNoteCardCallbacks,
  VoiceNoteCardData,
} from './ContactOneOffActionSectionPure';
import type { ContactSignalGridItem } from './ContactSignalsGrid';
import type { PreviousEmailThread } from './previousEmailThread';
import type {
  Cadence,
  CadenceStep,
  Contact,
  ContactDetailMutations,
  ContactDossier,
  ContactUser,
  ContactUserInteraction,
  HistoryData,
  MeetingBookedData,
  Org,
  PendingActionData,
  TeamUserOption,
} from './types';
import { CadenceStepSectionPure } from '../CadenceStep';
import { cn } from '../cn';
import { InfoBox } from '../InfoBox';
import Button from '../Input2/Button';
import CampaignButtonWithCadence from '../Input2/CampaignButtonWithCadence';
import StageButton from '../Input2/StageButton';
import ResumeOrStartFreshPopup, { shouldPromptResumeOrStartFresh } from '../Popup/ResumeOrStartFreshPopup';
import { ResponseSection } from '../ResponseSection';
import { SmallPadded } from '../Scrollable';
import { SidebarNotes } from '../SidebarNotes';
import { Arrows, General, Users } from '../StrokeIcons';
import { TeamMismatchBar } from '../TeamMismatchBar';
import { WaveSpinner } from '../WaveSpinner';
import { CampaignSwitcherPure } from './CampaignSwitcherPure';
import { ContactDataSectionPure } from './ContactDataSectionPure';
import { useContactDetailSlots } from './contactDetailSlots';
import { ContactDetailTabs } from './ContactDetailTabs';
import { ContactInteractSectionPure } from './ContactInteractSectionPure';
import { ContactOneOffActionSectionPure } from './ContactOneOffActionSectionPure';
import DetailHeader from './DetailHeader';
import DetailHeaderContactPure from './DetailHeaderContactPure';

// ============================================================================
// Types
// ============================================================================

export type TabId = 'action' | 'interact' | 'data';

/**
 * Cadence data from the contact query
 */
export type ContactActiveCadence = {
  cadence: Cadence & {
    state?: string;
    templateId?: string | null;
    nextStepTypes?: string[];
    nextRunTime?: Date | string | null;
    nextStepBody?: string | null;
    nextStepSubject?: string | null;
  };
  cadenceSteps: (CadenceStep & { errorMessage?: string | null })[] | null;
  cadenceContactUser?: ContactUser | null;
};

/**
 * Full contact detail data from the contact.byId query
 */
export type ContactDetailData = {
  contactV2: Contact;
  team: { id: string; name: string };
  authedUser: ContactUser;
  contactOrg: (Org & { primaryFields?: { name?: { data?: string } }; primaryPictureUrl?: string | null }) | null;
  responses: ContactUserInteraction[];
  activeCadence: ContactActiveCadence | null;
};

/**
 * Data for pure action section component
 */
export type ActionSectionData = {
  userTeam: ActionSectionUserTeam;
  cadenceId?: string | null;
  pendingStep?: PendingStep | null;
  scheduleStatus?: ScheduleStatus | null;
  linkedinSteps: ActionStepConfig[];
  otherSteps: ActionStepConfig[];
  currentAction?: string;
  isAddingToTakeAction?: boolean;
  isDeletingPendingStep?: boolean;
  isSendingNowPendingStep?: boolean;
  isUnipileConnecting?: boolean;
  isSubmitting?: boolean;
  atVariables?: Record<string, string>;
  superAdminAiPreview?: {
    onGenerate: () => void;
    isGenerating: boolean;
    prompt: string | null;
    message: string | null;
    error?: string | null;
    instructions: string;
    onChangeInstructions: (value: string) => void;
    shouldSend?: { shouldSend: boolean; reasoning: string } | null;
  };
};

/**
 * Retry state for attachment processing
 */
export type RetryState = 'idle' | 'pending' | 'success' | 'error';

export type ContactDetailPageCoreProps = {
  // Contact identifier (available even before data loads)
  contactId: string;

  authTeamId: string;
  authTeamName: string;
  authUserId: string;
  authUnipileAccountId?: string;
  authUnipileAccountError?: string | null;
  authIsPremium?: boolean;
  authCreditsRemaining: number;

  // Data
  data: ContactDetailData | null;
  isLoading: boolean;

  // Supplementary data
  stages: Stage[];
  stagesLoading: boolean;
  history: HistoryData;
  historyIsLoading: boolean;
  meetingBookedDetails: MeetingBookedData[] | null;
  pendingAction?: PendingActionData | null;
  teamUsers?: TeamUserOption[];

  // Tab state (can be controlled externally or internally)
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;

  // Interact-tab campaign-steps density (Minimal/Expanded toggle). Both
  // optional; when `onInteractDisplayChange` is omitted the toggle is hidden
  // and the cadence always renders expanded.
  interactDisplay?: InteractDisplayMode;
  onInteractDisplayChange?: (mode: InteractDisplayMode) => void;

  // All mutations as callbacks
  mutations: ContactDetailMutations;

  /** An async enrichment run is live for this contact — shows the "Enrichment
   * in progress" banner above the tabs (docs/WATERFALL_ENRICHMENT.md §8).
   * Optional: surfaces that haven't wired the polling hook show no banner. */
  enrichmentInFlight?: boolean;

  // Campaign button data
  campaignTemplates: TemplateOption[];
  campaignOwners: OwnerOption[];
  lastUsedTemplateId?: string | null;
  lastUsedTemplateName?: string | null;
  /**
   * Live sequence enrollments for this contact (the graph-based journey model). Rendered as
   * "in sequence X" pills that block the enroll dropdown, so a contact already on a journey can't be
   * double-enrolled. Surfaces that don't wire the query pass nothing → no pills, no block.
   */
  activeSequences?: ActiveSequenceEnrollment[];
  stagingCandidates?: StagingCandidate[];
  onStagingClick?: (candidate: StagingCandidate) => void;
  onStagingDelete?: (candidate: StagingCandidate) => Promise<void>;
  isStagingDeletingCandidateId?: number | null;
  /** Reassign a staging candidate's owner from the campaign-badge avatar dropdown. */
  onStagingChangeOwner?: (candidate: StagingCandidate, newUserId: string) => Promise<void>;
  /** Forwarded into ContactInteractSectionPure's candidate-in-progress card. */
  onStagingRetry?: (candidate: StagingCandidate) => Promise<unknown>;
  stagingRetryingCandidateId?: number | null;

  // Action section data (pure approach - preferred)
  actionSectionData?: ActionSectionData | null;

  // Action section callbacks (pure approach)
  onActionChange?: (action: string | undefined) => void;
  onSubmitActionStep?: (step: ActionStep, sendMode: SendMode) => Promise<void>;
  onAddToTakeAction?: (onlyNewPeople: boolean) => Promise<void>;
  addToTakeActionOnlyNewPeople?: boolean;
  onChangeAddToTakeActionOnlyNewPeople?: (onlyNewPeople: boolean) => void;
  onDeletePendingStep?: () => void;
  onSendNowPendingStep?: () => void;
  onUnipileConnect?: () => void;
  getInitialActionStep?: (type: StepType) => ActionStep;

  // Step editor data (pure approach - renders CadenceStepSectionPure directly)
  stepEditorData?: StepEditorData;
  stepEditorCallbacks?: StepEditorCallbacks;
  autoGenerateAi?: boolean;
  onAutoGenerateAiChange?: (value: boolean) => void;

  // Action card props
  /** Whether the current platform is macOS-like (for facetime button) */
  isMacLike?: boolean;
  /** Phone number for call step type */
  tel?: string | null;
  /** Callback when manual call is recorded (for tel: links) */
  onRecordManualCall?: () => void;
  /** Injected in-app click-to-call trigger; shared by dashboard + extension surfaces. */
  clickToCall?: ReactNode;
  /**
   * Injected "Calling from … / Change" caption for the contact's pinned from-number, letting the rep
   * pin it before dialing. Shared by dashboard + extension surfaces.
   */
  dialFromNumber?: ReactNode;
  /** Injected "Schedule a call" affordance — rendered as a row below the reminder card. */
  scheduleCall?: ReactNode;
  /**
   * Injected dialer factory for a `call`-type cadence reminder. Returns the surface's
   * click-to-call button wired to the given step id, so logging the outcome completes the step.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => ReactNode;
  /** Completes a `call`-type cadence reminder the rep handled outside the dialer; shared by dashboard + extension. */
  onMarkCallStepDone?: (args: {
    cadenceStepId: string;
    outcome: string;
    notes?: string;
    callbackAt?: number;
  }) => Promise<void> | void;
  /** Injected dialer factory for a standalone `call`-kind reminder; shared by dashboard + extension. */
  renderReminderCallButton?: (args: { globalReminderId: string }) => ReactNode;

  // Reminder card (pure approach)
  reminderCardData?: ReminderCardData;
  reminderCardCallbacks?: ReminderCardCallbacks;

  // Voice note card (pure approach)
  voiceNoteCardData?: VoiceNoteCardData;
  voiceNoteCardCallbacks?: VoiceNoteCardCallbacks;
  /** Recorded video notes — pass only when the team has `videoNotesEnabled`. */
  videoNoteCardCallbacks?: VideoNoteCardCallbacks;
  /** Optional replacement for the built-in `VoiceRecorder` in the voice-note card.
      Used by the Chrome extension to route mic capture through an offscreen document. */
  voiceRecorderComponent?: ComponentType<VoiceRecorderProps>;

  /** File attachment for LinkedIn message/inmail sends. Omit on surfaces that don't
      support attachments (currently wired only on the Next.js contact detail page). */
  messageAttachment?: MessageAttachmentState;

  // Cadence section data
  cadenceStepsGraph?: CadenceGraph | null;
  /**
   * The cadence OWNER's latest email thread with this contact, or null. The campaign step editors
   * offer "continue previous thread" against it — the send replies into that owner's mailbox.
   * Derive it with `getPreviousEmailThread(history, <cadence owner's user id>)`.
   */
  cadenceEmailThread?: PreviousEmailThread | null;
  cadenceTemplateInfo?: TemplateInfo | null;
  cadenceDocuments?: DocumentData[];

  // Cadence section callbacks
  onCadenceCreateStep?: (data: CadenceStepCreateInput) => unknown;
  onCadenceUpdateStep?: (stepId: string, data: CadenceStepUpdateInput) => unknown;
  onCadenceDeleteStep?: (stepId: string) => unknown;
  onCadenceChangeStepType?: (stepId: string, type: string) => unknown;
  onCadenceResolveStep?: (stepId: string, resolution: StepResolution) => unknown;
  onToggleInmailOpen?: (nodeId: string, onlyOpenLink: boolean) => unknown;

  // Smart-campaign AI Message Section removed; kept as a no-op placeholder.
  aiMessageData?: AIMessageSectionData | null;

  renderSiftConditionBuilder?: (props: { value: SiftConfig; onChange: (value: SiftConfig) => unknown }) => ReactNode;

  /**
   * Touch-campaign graph renderer, forwarded to ContactInteractSectionPure.
   * When provided, the interact tab shows the editable touch node-canvas in
   * place of the vertical cadence timeline. Injected as a render prop because
   * the graph component lives in `apps/nextjs` and can't be imported into this
   * shared package.
   */
  renderTouchCampaignGraph?: () => ReactNode;

  /** Per-contact sequence graph renderer (contact enrolled in ≥1 sequence). Passed through to the interact
   *  tab; renders independently of a cadence. Injected from `apps/nextjs` (same layering as above). */
  renderSequenceGraph?: () => ReactNode;
  /** The interact tab's GRAPH view for a contact in a sequence — see `ContactInteractSectionPure`. */
  renderSequenceCanvas?: ContactInteractSectionPureProps['renderSequenceCanvas'];
  /** List / Graph for a contact in a sequence (persisted by the host). */
  sequenceView?: SequenceViewMode;
  onSequenceViewChange?: (view: SequenceViewMode) => void;

  // Reminder editor — list of available reminder actions for the team and a
  // create-on-the-fly callback. Threaded straight through to the cadence
  // card's inline reminder editor.
  reminderActions?: { id: string; name: string }[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  onReminderError?: (message: string) => void;

  // Toast callbacks for field copy operations
  onFieldCopied?: (data: string) => unknown;
  onFieldCopyFailed?: (data: string) => unknown;

  // UI options
  onClose?: () => unknown;
  onClickOrg?: () => unknown;
  hideCloseButton?: boolean;
  hideActionTab?: boolean;
  hideDossierTab?: boolean;
  honorificAgeThreshold?: number | null;
  className?: string;

  // Superadmin features
  onCopyToSuperadminTeam?: () => unknown;
  isCopyPending?: boolean;
  superadminName?: string;
  superadminTeamName?: string;
  onDownloadRecentPosts?: () => void;
  isDownloadingRecentPosts?: boolean;
  onDeleteContact?: () => void;
  isDeletingContact?: boolean;

  // Images inspector (collapsed section above Delete Contact). Undefined → hidden.
  contactImages?: ContactImageVariants | null;
  isContactImagesLoading?: boolean;

  // LinkedIn Identifiers section data and callbacks
  linkedInMappings: LinkedInMapping[];
  linkedinUnavailable?: boolean;
  isLinkedInMappingsLoading: boolean;
  onDeleteLinkedInMapping: (mappingId: number) => unknown;
  onVerifyLinkedInMapping: (mappingId: number) => unknown;
  onCopyLinkedInMapping: (foreignId: string) => unknown;
  onAddLinkedInMapping: (foreignSource: 'linkedin' | 'salesnav', foreignId: string) => void;
  isLinkedInMappingDeleting: boolean;
  isLinkedInMappingVerifying: boolean;
  isLinkedInMappingAdding: boolean;

  // Contact tags render prop
  renderContactTags?: () => ReactNode;

  // Signals (rendered as a 3x3 grid just below the Track LinkedIn toggle when signals is provided)
  signals?: ContactSignalGridItem[];
  isSignalsLoading?: boolean;
  lastUnipileFetchAt?: Date | string | null;
  ignoredAspectKeys?: ReadonlySet<string>;

  // Dossier — the condensed research summary rendered in the quick-actions
  // (Take Action) tab via DossierSummaryPure. Null when no dossier exists;
  // isDossierGenerating drives the spinner UI for the in-progress case, and
  // onRegenerateDossier force-refreshes it (Regenerate button in the summary).
  dossier?: ContactDossier | null;
  isDossierGenerating?: boolean;
  onRegenerateDossier?: () => Promise<unknown>;
  isRegeneratingDossier?: boolean;

  // Employment & Education history (data tab)
  /**
   * The as-you-type template picker for the one-off composer. Each surface
   * supplies its own data source; the rendered picker is shared.
   */
  renderSuggestTemplates?: (props: {
    value: string;
    atVariables: Record<string, string>;
    documents: EditorDocument[];
    onClick: (body2: string, subject?: string) => void;
    forceShow: boolean;
  }) => ReactNode;
  experiences?: ContactExperienceItem[];
  education?: ContactEducationItem[];
  isHistoryLoading?: boolean;
  onAddExperience?: (input: AddExperienceInput) => Promise<void>;
  orgSearch?: (query: string) => Promise<OrgSearchOption[]>;
  onDeleteExperience?: (experienceId: string) => Promise<void>;

  // View campaigns callback
  onViewAllCampaigns?: () => void;

  // View required actions callback
  onViewRequiredActions?: () => void;

  // Click cadence in history to view it
  onClickCadence?: (cadenceId: string) => void;

  // Active-campaign switcher — shown in the header when the contact is enrolled
  // in 2+ active campaigns, so the rep can see there's more than one and switch
  // which campaign the detail view (chip + steps) reflects. `selectedCampaignId`
  // is the one currently shown; `onSelectCampaign` re-pins the detail to another.
  // Optional: surfaces that don't wire these simply won't render the switcher.
  campaigns?: SwitcherCampaign[];
  selectedCampaignId?: string | null;
  onSelectCampaign?: (cadenceId: string) => void;

  // Team context for warnings
  contactTeamId?: string;
  contactTeamName?: string;

  // Respond step section
  respondStep?: StepData | null;
  /**
   * The email thread this user can continue with the contact, or null when there is none. Non-null
   * gates the respond composer's "Reply in thread" toggle; its `subject` fills the thread banner.
   * Derive it with `getPreviousEmailThread`.
   */
  respondPreviousEmailThread?: PreviousEmailThread | null;
  onCancelRespond?: () => void;
  onClickToRespond?: (type: string) => void;
  // Respond section data
  respondDocuments?: EditorDocument[];
  respondAiChoices?: AiChoice[];
  respondAiIndex?: number;
  respondAiCommand?: string;
  respondAiLoading?: boolean;
  respondStepsList?: StepListItem[];
  respondIsSubmitting?: boolean;
  respondIsAiGenerating?: boolean;
  // Respond section callbacks
  onRespondSubmit?: (step: StepData) => Promise<void>;
  onRespondChange?: (changes: StepChanges) => void;
  onRespondGenerateAi?: (empty?: boolean) => Promise<void>;
  onRespondAiCommandChange?: (command: string) => void;
  onRespondAiIndexChange?: (index: number) => void;
  onRespondToast?: (message: string, type: 'error' | 'success') => void;

  // Step tracking (for CadenceSection highlighting)
  updatedStepId?: string;
  setUpdatedStepId?: (id: string | undefined) => void;

  // Attachment retry states
  retryStates?: Record<string, RetryState>;
  retryErrors?: Record<string, string | null>;
  onRetryAttachment?: (interactionId: string) => void;

  // Transcription request states
  transcriptionRequestStates?: Record<string, 'idle' | 'pending' | 'success' | 'error'>;
  onRequestTranscription?: (interactionId: string) => void;

  // @variables for message interpolation
  atVariables?: Record<string, string>;

  // Org navigation
  orgFirst?: boolean;
  onOrgNavigate?: (orgId: string) => void;

  // Additional buttons to render in the header (next to stage button)
  additionalHeaderButtons?: ReactNode;
  voiceEnabled?: boolean;
  voiceCallingEnabled?: boolean;
  /** Origin for the "See in template" link — see CadenceSectionPureProps. */
  templateLinkBaseUrl?: string;
};

// ============================================================================
// ContactDetailPageCore
// ============================================================================

export function ContactDetailPageCore({
  contactId,
  authTeamId,
  authTeamName,
  authUserId,
  authIsPremium,
  authUnipileAccountId,
  authUnipileAccountError,
  authCreditsRemaining,
  data,
  isLoading,
  stages,
  stagesLoading,
  history,
  historyIsLoading,
  meetingBookedDetails,
  pendingAction,
  activeTab: activeTabProp,
  onTabChange,
  interactDisplay,
  onInteractDisplayChange,
  mutations,
  enrichmentInFlight,
  campaignTemplates,
  campaignOwners,
  lastUsedTemplateId,
  lastUsedTemplateName,
  activeSequences,
  stagingCandidates,
  onStagingClick,
  onStagingDelete,
  isStagingDeletingCandidateId,
  onStagingChangeOwner,
  onStagingRetry,
  stagingRetryingCandidateId,
  actionSectionData,
  onActionChange,
  onSubmitActionStep,
  onAddToTakeAction,
  addToTakeActionOnlyNewPeople = true,
  onChangeAddToTakeActionOnlyNewPeople,
  onDeletePendingStep,
  onSendNowPendingStep,
  onUnipileConnect,
  getInitialActionStep,
  stepEditorData,
  stepEditorCallbacks,
  autoGenerateAi,
  onAutoGenerateAiChange,
  isMacLike,
  tel,
  onRecordManualCall,
  clickToCall,
  dialFromNumber,
  scheduleCall,
  renderCadenceCallButton,
  onMarkCallStepDone,
  renderReminderCallButton,
  reminderCardData,
  reminderCardCallbacks,
  voiceNoteCardData,
  voiceNoteCardCallbacks,
  videoNoteCardCallbacks,
  voiceRecorderComponent,
  messageAttachment,
  cadenceStepsGraph,
  cadenceEmailThread,
  cadenceTemplateInfo,
  cadenceDocuments,
  onCadenceCreateStep,
  onCadenceUpdateStep,
  onCadenceDeleteStep,
  onCadenceChangeStepType,
  onCadenceResolveStep,
  onToggleInmailOpen,
  aiMessageData: _aiMessageData,
  renderSiftConditionBuilder,
  renderTouchCampaignGraph,
  renderSequenceGraph,
  renderSequenceCanvas,
  sequenceView,
  onSequenceViewChange,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  onFieldCopied,
  onFieldCopyFailed,
  onClose,
  onClickOrg,
  hideCloseButton,
  hideActionTab,
  hideDossierTab,
  honorificAgeThreshold,
  className,
  onCopyToSuperadminTeam,
  isCopyPending,
  superadminName,
  superadminTeamName,
  onDownloadRecentPosts,
  isDownloadingRecentPosts,
  onDeleteContact,
  isDeletingContact,
  contactImages,
  isContactImagesLoading,
  linkedInMappings,
  linkedinUnavailable,
  isLinkedInMappingsLoading,
  onDeleteLinkedInMapping,
  onVerifyLinkedInMapping,
  onCopyLinkedInMapping,
  onAddLinkedInMapping,
  isLinkedInMappingDeleting,
  isLinkedInMappingVerifying,
  isLinkedInMappingAdding,
  renderContactTags,
  signals,
  isSignalsLoading,
  lastUnipileFetchAt,
  ignoredAspectKeys,
  dossier = null,
  isDossierGenerating = false,
  onRegenerateDossier,
  isRegeneratingDossier = false,
  renderSuggestTemplates,
  experiences,
  education,
  isHistoryLoading,
  onAddExperience,
  orgSearch,
  onDeleteExperience,
  onViewAllCampaigns,
  onViewRequiredActions,
  onClickCadence,
  campaigns,
  selectedCampaignId,
  onSelectCampaign,
  contactTeamId,
  contactTeamName,
  respondStep,
  respondPreviousEmailThread,
  onCancelRespond,
  onClickToRespond,
  respondDocuments,
  respondAiChoices,
  respondAiIndex,
  respondAiCommand,
  respondAiLoading,
  respondStepsList,
  respondIsSubmitting,
  respondIsAiGenerating,
  onRespondSubmit,
  onRespondChange,
  onRespondGenerateAi,
  onRespondAiCommandChange,
  onRespondAiIndexChange,
  onRespondToast,
  updatedStepId: _updatedStepId,
  setUpdatedStepId: _setUpdatedStepId,
  retryStates,
  retryErrors,
  onRetryAttachment: _onRetryAttachment,
  transcriptionRequestStates,
  onRequestTranscription,
  atVariables: atVariablesProp,
  orgFirst: _orgFirst,
  onOrgNavigate: _onOrgNavigate,
  teamUsers,
  additionalHeaderButtons,
  voiceEnabled,
  voiceCallingEnabled,
  templateLinkBaseUrl,
}: ContactDetailPageCoreProps) {
  const slots = useContactDetailSlots();
  // Teammate display names, keyed by user id — resolves e.g. who condemned a phone
  // value in the data-section tooltips.
  const userNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of teamUsers ?? []) {
      const name = u.userTeamName?.trim() || u.userEmail;
      if (name) map[u.userId] = name;
    }
    return map;
  }, [teamUsers]);

  // Internal tab state (if not controlled). The default follows the contact:
  // with no active campaign there is nothing to show under Campaign & History
  // but an empty-state card, so open on Quick Actions instead. A tab the user
  // picks sticks for that contact only — the extension sidebar keeps this
  // component mounted as the rep moves between profiles, so the pick is tagged
  // with the contact it was made on and a new contact gets the default again.
  const [pickedTab, setPickedTab] = useState<{ contactId: string; tab: TabId } | null>(null);
  const hasActiveCadence = data?.activeCadence?.cadence != null;
  const defaultTab: TabId = hasActiveCadence ? 'interact' : 'action';
  const activeTab = activeTabProp ?? (pickedTab?.contactId === contactId ? pickedTab.tab : defaultTab);
  const handleTabChange = (tab: TabId) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setPickedTab({ contactId, tab });
    }
  };

  // Notes state
  const [notesOpen, setNotesOpen] = useState(false);

  // Response dismiss state
  const [isAckPending, setIsAckPending] = useState(false);

  // POS-2455 "Resume or start fresh?" prompt. Set when enrolling into a
  // template this rep already partially sent this contact, with the last send
  // older than the stale threshold. Recent history resumes silently (the
  // server skips already-sent steps either way).
  const [resumePrompt, setResumePrompt] = useState<{ templateId: string; summary: PriorSentStepsSummary } | null>(null);

  const handleEnrollInCampaign = async (templateId: string) => {
    const fetchPriorSentSteps = mutations.fetchPriorSentSteps;
    // Pre-flight only applies to plain campaign templates — switches resolve
    // their destination after research and sequences are a different engine.
    if (fetchPriorSentSteps == null || templateId.startsWith('switch:') || templateId.startsWith('sequence:')) {
      return mutations.enrollInCampaign(templateId);
    }
    // Fail open: a pre-flight error must never block enrolling (the server
    // guard still protects against re-sends).
    const summary = await fetchPriorSentSteps(templateId).catch(() => null);
    if (summary == null || !shouldPromptResumeOrStartFresh(summary)) {
      return mutations.enrollInCampaign(templateId);
    }
    setResumePrompt({ templateId, summary });
  };

  // Enhance re-verifies the contact on LinkedIn. When the profile is confirmed dead and
  // unrepairable, the server removes the contact in the same call (no confirm popup) and
  // reports `deleted` — so all we do here is close the now-gone detail view.
  const handleEnhance = async () => {
    const { deleted } = await mutations.enhanceContact();
    if (deleted) onClose?.();
  };

  // Team mismatch check
  const isTeamMismatch = contactTeamId != null && contactTeamId !== authTeamId;

  // ============================================================================
  // Loading / not-found are handled by separate components — see
  // `ContactDetailPageSkeleton` and `ContactDetailPageNotFound`. The host
  // is expected to render those when `isLoading` is true or `data` is
  // `null`, so by this point we can assume we have real data to show.
  // ============================================================================
  if (isLoading || data == null) {
    // Defensive guard so existing callers that haven't migrated yet
    // don't crash — but ALL new code should switch to the skeleton /
    // not-found components.
    return null;
  }

  // ============================================================================
  // Extract Data
  // ============================================================================
  const contact = data.contactV2;
  // Note: data.team available for team mismatch warnings
  const contactOrg = data.contactOrg;
  const responses = data.responses ?? [];
  const activeCadence = data.activeCadence;

  const contactResponse = responses[0];
  const notes = contact.notes;
  const stepError = activeCadence?.cadenceSteps?.find((s) => s.errorMessage)?.errorMessage;
  const hasNotes = notes != null && notes.length > 0 && notes !== '<p></p>' && notes !== '<p><br></p>';
  const hasCadence = activeCadence?.cadence != null;

  // Contact name and display info
  const contactName = [contact.primaryFields?.firstName?.data, contact.primaryFields?.lastName?.data]
    .filter(Boolean)
    .join(' ');
  const avatarUrl = contact.primaryPictureUrl;
  const headline = contact.primaryFields?.headline?.data;
  const title = contact.primaryFields?.title?.data;
  const linkedinUsername = contact.liUsername ?? contact.liSalesNavId;
  const hasValidLinkedInUrl =
    linkedinUsername != null && linkedinUsername !== 'null' && linkedinUsername !== 'undefined';
  const linkedinUrl = hasValidLinkedInUrl ? `https://www.linkedin.com/in/${linkedinUsername}/` : undefined;

  // Age and honorific display
  // Use displayHonorific from server if available (calculated based on age threshold), otherwise calculate locally
  const minAgeEstimate = contact.ageEstimate?.minAge;
  const serverDisplayHonorific = (contact as { displayHonorific?: boolean }).displayHonorific;
  const displayHonorific =
    serverDisplayHonorific ??
    (honorificAgeThreshold != null && minAgeEstimate != null && minAgeEstimate >= honorificAgeThreshold);

  // Build cadence data for ContactInteractSectionPure
  const cadenceData: CadenceData | undefined = activeCadence
    ? {
        cadence: activeCadence.cadence,
        cadenceSteps: activeCadence.cadenceSteps,
      }
    : undefined;

  // At variables from parent
  const atVariables = atVariablesProp ?? {};

  // Notes change handler
  const handleNotesChange = async (newNotes: string) => {
    await mutations.updateNotes(newNotes);
  };

  // Response dismiss handler
  const handleDismissResponse = async () => {
    setIsAckPending(true);
    try {
      await mutations.ackResponse(contactId, true);
    } finally {
      setIsAckPending(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={cn('flex h-full flex-col overflow-hidden', className)}>
      {/* Team mismatch warning */}
      {isTeamMismatch && contactTeamName && (
        <TeamMismatchBar thing="contact" ownerTeamName={contactTeamName} currentTeamName={authTeamName} />
      )}

      {/* Response section (if contact has a response) */}
      {contactResponse != null && (
        <ResponseSection
          contactFirstName={contact.primaryFields?.firstName?.data}
          cadenceState={activeCadence?.cadence?.state}
          responseAction={contactResponse.interactionType}
          responseDate={contactResponse.createdAt}
          responseSubject={contactResponse.subject}
          responseBody={contactResponse.body}
          onDismissResponse={handleDismissResponse}
          isLoading={isAckPending}
          onRespond={() => onClickToRespond?.(contactResponse.interactionType)}
        />
      )}

      {/* Header */}
      <DetailHeader
        isPaused={activeCadence?.cadence?.state === 'paused'}
        className="bg-slate-50 dark:bg-slate-900"
        onClose={onClose}
        hideCloseButton={hideCloseButton}
        buttons={
          <>
            <StageButton
              size="xs"
              stages={stages}
              stageId={contact.stageId ?? undefined}
              disabled={stagesLoading}
              onChange={mutations.setContactStage}
              status={mutations.isSettingContactStage ? 'updating' : undefined}
              icon={<Users.Users01 />}
            />
            {additionalHeaderButtons}
          </>
        }
        footer={
          <div className="flex flex-col px-4 py-2">
            <div className="flex items-center justify-end gap-2 pb-5">
              <CampaignButtonWithCadence
                // Slot the active-campaign switcher chevron flush against the pill
                // (rendered as one segmented control) instead of floating separately.
                // Only when there are 2+ active campaigns, so the pill keeps its
                // rounded-left edge in the common single-campaign case.
                leadingSegment={
                  onSelectCampaign != null && (campaigns?.length ?? 0) > 1 ? (
                    <CampaignSwitcherPure
                      campaigns={campaigns ?? []}
                      selectedCampaignId={selectedCampaignId ?? null}
                      onSelectCampaign={onSelectCampaign}
                    />
                  ) : undefined
                }
                cadence={
                  activeCadence?.cadence
                    ? {
                        id: activeCadence.cadence.id,
                        state: activeCadence.cadence.state ?? 'not-started',
                        templateId: activeCadence.cadence.templateId ?? null,
                        name: activeCadence.cadence.name ?? '',
                        interruptMessage: null,
                        interruptReason: null,
                      }
                    : undefined
                }
                cadenceUserId={activeCadence?.cadenceContactUser?.userId}
                cadenceOwnerName={activeCadence?.cadenceContactUser?.name}
                cadenceOwnerProfilePicture={activeCadence?.cadenceContactUser?.profilePicture}
                currentUserId={authUserId}
                templates={campaignTemplates}
                owners={campaignOwners}
                lastUsedTemplateId={lastUsedTemplateId}
                lastUsedTemplateName={lastUsedTemplateName}
                stepError={stepError}
                onEnroll={handleEnrollInCampaign}
                onPause={mutations.pauseCadence}
                onResume={mutations.resumeCadence}
                onReattach={mutations.reattachCadence}
                onDelete={mutations.deleteCadence}
                onChangeOwner={mutations.changeCadenceOwner}
                onOpenTemplate={mutations.openCampaignTemplate}
                isPending={mutations.isCampaignPending}
                isDeleting={mutations.isCampaignDeleting}
                stagingCandidates={stagingCandidates}
                onStagingClick={onStagingClick}
                onStagingDelete={onStagingDelete}
                isStagingDeletingCandidateId={isStagingDeletingCandidateId}
                onStagingChangeOwner={onStagingChangeOwner}
                activeSequences={activeSequences}
                onSequenceUnenroll={mutations.unenrollSequence}
                isSequenceUnenrollingId={mutations.isSequenceUnenrollingId}
                onSequenceTogglePause={mutations.toggleSequencePause}
                isSequenceTogglingPauseId={mutations.isSequenceTogglingPauseId}
              />
            </div>
            {enrichmentInFlight && (
              <div className="mb-1.5 flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                <span className="size-1.5 animate-pulse rounded-full bg-blue-500" />
                Enrichment in progress — results will appear automatically.
              </div>
            )}
            <ContactDetailTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              hasNotes={hasNotes}
              notesOpen={notesOpen}
              onToggleNotes={() => setNotesOpen((o) => !o)}
              hideActionTab={hideActionTab}
              rightAction={(() => {
                const hasUnipileAccount = authUnipileAccountId != null;
                const liDisconnected = hasUnipileAccount && authUnipileAccountError != null;
                if (hasUnipileAccount && !liDisconnected) {
                  return (
                    <Button
                      size="sm"
                      shape="pill"
                      color="blue"
                      tone="outline"
                      disabled={
                        mutations.isRefreshing || mutations.isEnhancing || !(contact.liUsername || contact.liSalesNavId)
                      }
                      icon={mutations.isRefreshing || mutations.isEnhancing ? <WaveSpinner /> : <Arrows.RefreshCw04 />}
                      onClick={async () => {
                        await mutations.refreshFromLinkedIn();
                        if ((authIsPremium ?? false) && authCreditsRemaining > 0) {
                          await handleEnhance();
                        }
                      }}
                      tooltip={
                        <span className="block">
                          <span className="block">
                            {(authIsPremium ?? false) && authCreditsRemaining > 0
                              ? `Refresh & enrich (uses 1 of ${authCreditsRemaining.toLocaleString()} credits)`
                              : 'Refresh LinkedIn data only — no enrichment credits remaining'}
                          </span>
                          <span className="mt-0.5 block opacity-70">
                            {lastUnipileFetchAt != null
                              ? `Last LinkedIn fetch: ${new Date(lastUnipileFetchAt).toLocaleString()}`
                              : 'Never fetched from LinkedIn'}
                          </span>
                        </span>
                      }
                    />
                  );
                }
                if (liDisconnected && onUnipileConnect) {
                  return (
                    <Button
                      size="sm"
                      shape="pill"
                      color="yellow"
                      tone="outline"
                      icon={<Arrows.RefreshCw04 />}
                      onClick={onUnipileConnect}
                      tooltip="LinkedIn account disconnected — click to reconnect and refresh"
                    />
                  );
                }
                if (onUnipileConnect) {
                  return (
                    <Button
                      size="sm"
                      shape="pill"
                      color="yellow"
                      tone="outline"
                      icon={<General.Link01 />}
                      onClick={onUnipileConnect}
                      tooltip="Connect your LinkedIn account via API to enable data refresh"
                    />
                  );
                }
                return null;
              })()}
            />
          </div>
        }>
        <DetailHeaderContactPure
          name={contactName}
          // Mirror the search-card behavior in ContactCardImage: pass the
          // contact photo first, then the org logo as a fallback. Avatar
          // walks the list on image-load errors and uses initials only when
          // every entry fails. Without this fallback, LinkedIn signed URLs
          // that have expired (the `e=…` timestamp) drop the avatar to JB-
          // style initials while the card still shows the org badge.
          avatarUrls={[avatarUrl, contactOrg?.primaryPictureUrl]}
          linkedinUrl={linkedinUrl}
          subtitle={title ?? headline}
          orgName={contactOrg?.primaryFields?.name?.data}
          orgAvatarUrl={contactOrg?.primaryPictureUrl}
          onClickOrg={onClickOrg}
          isConnected={data.authedUser?.liConnected ?? undefined}
          displayHonorific={displayHonorific}
          honorificTitle={contact.primaryFields?.honorificTitle?.data}
          nickname={contact.primaryFields?.nickname?.data}
          cadenceContactUser={
            activeCadence?.cadenceContactUser
              ? {
                  userId: activeCadence.cadenceContactUser.userId,
                  profilePicture: activeCadence.cadenceContactUser.profilePicture,
                  userTeamName: activeCadence.cadenceContactUser.teamName,
                  userEmail: activeCadence.cadenceContactUser.email ?? '',
                }
              : undefined
          }
        />
      </DetailHeader>

      {/* Host-provided context under the header (e.g. RIA Search's firm / fit / warm-path section) */}
      {slots.belowHeader}

      {/* Content area */}
      <div className="relative flex h-0 w-full grow flex-col overflow-hidden">
        {notesOpen && (
          <SidebarNotes
            recordId={contactId}
            title="Notes"
            initialValue={notes ?? ''}
            onDebounceChange={handleNotesChange}
          />
        )}

        {/* Action Tab */}
        {activeTab === 'action' && isTeamMismatch && contactTeamName && (
          <SmallPadded>
            <InfoBox title="Actions disabled">
              <div>switch to &apos;{contactTeamName}&apos; team to perform actions</div>
            </InfoBox>
          </SmallPadded>
        )}
        {/* Pure action section (preferred) */}
        {activeTab === 'action' && !isTeamMismatch && actionSectionData && onActionChange && onAddToTakeAction && (
          <ContactOneOffActionSectionPure
            renderSuggestTemplates={renderSuggestTemplates}
            key={contactId}
            contactId={contactId}
            contactPrimaryFields={contact.primaryFields}
            liConnectedAt={data.authedUser.liConnectedAt}
            emailCredentialId={data.authedUser.emailCredentialId}
            userTeam={actionSectionData.userTeam}
            cadenceId={actionSectionData.cadenceId}
            pendingStep={actionSectionData.pendingStep}
            scheduleStatus={actionSectionData.scheduleStatus}
            linkedinSteps={actionSectionData.linkedinSteps}
            otherSteps={actionSectionData.otherSteps}
            currentAction={actionSectionData.currentAction}
            onActionChange={onActionChange}
            onSubmitStep={onSubmitActionStep ?? (async () => {})}
            onAddToTakeAction={onAddToTakeAction}
            addToTakeActionOnlyNewPeople={addToTakeActionOnlyNewPeople}
            onChangeAddToTakeActionOnlyNewPeople={onChangeAddToTakeActionOnlyNewPeople ?? (() => {})}
            onDeletePendingStep={onDeletePendingStep}
            isDeletingPendingStep={actionSectionData.isDeletingPendingStep}
            onSendNowPendingStep={onSendNowPendingStep}
            isSendingNowPendingStep={actionSectionData.isSendingNowPendingStep}
            onUnipileConnect={onUnipileConnect}
            isUnipileConnecting={actionSectionData.isUnipileConnecting}
            isAddingToTakeAction={actionSectionData.isAddingToTakeAction}
            isSubmitting={actionSectionData.isSubmitting}
            atVariables={actionSectionData.atVariables}
            getInitialStep={getInitialActionStep}
            // Step editor data (pure approach)
            stepEditorData={stepEditorData}
            stepEditorCallbacks={stepEditorCallbacks}
            autoGenerateAi={autoGenerateAi}
            onAutoGenerateAiChange={onAutoGenerateAiChange}
            // Action card props
            isMacLike={isMacLike}
            tel={tel}
            onRecordManualCall={onRecordManualCall}
            clickToCall={clickToCall}
            dialFromNumber={dialFromNumber}
            scheduleCall={scheduleCall}
            renderCadenceCallButton={renderCadenceCallButton}
            onMarkCallStepDone={onMarkCallStepDone}
            renderReminderCallButton={renderReminderCallButton}
            reminderCardData={reminderCardData}
            reminderCardCallbacks={reminderCardCallbacks}
            voiceNoteCardData={voiceNoteCardData}
            voiceNoteCardCallbacks={voiceNoteCardCallbacks}
            videoNoteCardCallbacks={videoNoteCardCallbacks}
            voiceRecorderComponent={voiceRecorderComponent}
            messageAttachment={messageAttachment}
            superAdminAiPreview={actionSectionData.superAdminAiPreview}
            // Dossier summary — surfaced inline in the quick actions (above the
            // superadmin preview), reusing the same data the Dossier tab renders.
            // Gated by the same flag as the Dossier tab (hideDossierTab).
            showDossier={!hideDossierTab}
            dossier={dossier}
            dossierGenerating={isDossierGenerating}
            onGenerateDossier={onRegenerateDossier ? () => void onRegenerateDossier() : undefined}
            isGeneratingDossier={isRegeneratingDossier}
          />
        )}

        {/* Interact Tab */}
        {activeTab === 'interact' && (
          <>
            <ContactInteractSectionPure
              currentUserId={authUserId}
              contactId={contactId}
              contactPrimaryFields={contact.primaryFields}
              contactLiAbout={contact.liAbout}
              contactAlreadyConnectedOnLinkedIn={data.authedUser?.liConnectedAt != null}
              cadenceData={cadenceData}
              stagingCandidates={stagingCandidates}
              onStagingClick={onStagingClick}
              onStagingRetry={onStagingRetry}
              stagingRetryingCandidateId={stagingRetryingCandidateId}
              org={contactOrg ?? undefined}
              history={history}
              historyIsLoading={historyIsLoading}
              pendingStep={actionSectionData?.pendingStep ?? null}
              scheduleStatus={actionSectionData?.scheduleStatus ?? null}
              onDeletePendingStep={onDeletePendingStep}
              isDeletingPendingStep={actionSectionData?.isDeletingPendingStep}
              onSendNowPendingStep={onSendNowPendingStep}
              isSendingNowPendingStep={actionSectionData?.isSendingNowPendingStep}
              onClickToRespond={onClickToRespond}
              onClickCadence={onClickCadence}
              atVariables={atVariables}
              onMarkMeetingBooked={mutations.markMeetingBooked}
              isMarkingMeetingBooked={mutations.isMarkingMeetingBooked}
              onResyncEmail={mutations.resyncEmail}
              isResyncingEmail={mutations.isResyncingEmail}
              onRetryAttachment={mutations.retryAttachment}
              retryStates={retryStates}
              retryErrors={retryErrors}
              onRequestTranscription={onRequestTranscription}
              transcriptionRequestStates={transcriptionRequestStates}
              cadenceStepsGraph={cadenceStepsGraph}
              cadenceEmailThread={cadenceEmailThread}
              cadenceTemplateInfo={cadenceTemplateInfo}
              cadenceDocuments={cadenceDocuments}
              onCadenceCreateStep={onCadenceCreateStep}
              onCadenceUpdateStep={onCadenceUpdateStep}
              onCadenceDeleteStep={onCadenceDeleteStep}
              onCadenceChangeStepType={onCadenceChangeStepType}
              onCadenceResolveStep={onCadenceResolveStep}
              onToggleInmailOpen={onToggleInmailOpen}
              renderSiftConditionBuilder={renderSiftConditionBuilder}
              renderTouchCampaignGraph={renderTouchCampaignGraph}
              renderSequenceGraph={renderSequenceGraph}
              renderSequenceCanvas={renderSequenceCanvas}
              sequenceView={sequenceView}
              onSequenceViewChange={onSequenceViewChange}
              reminderActions={reminderActions}
              reminderActionsLoading={reminderActionsLoading}
              onCreateReminder={onCreateReminder}
              reminderCreating={reminderCreating}
              onReminderError={onReminderError}
              voiceEnabled={voiceEnabled}
              voiceCallingEnabled={voiceCallingEnabled}
              renderCadenceCallButton={renderCadenceCallButton}
              onMarkCallStepDone={onMarkCallStepDone}
              templateLinkBaseUrl={templateLinkBaseUrl}
              interactDisplay={interactDisplay}
              onInteractDisplayChange={onInteractDisplayChange}
            />
          </>
        )}

        {/* Data Tab */}
        {activeTab === 'data' && (
          <>
            <ContactDataSectionPure
              contact={contact}
              userNamesById={userNamesById}
              authUserId={authUserId}
              authIsPremium={authIsPremium ?? false}
              authCreditsRemaining={authCreditsRemaining}
              authUnipileAccountId={authUnipileAccountId}
              authContactDisqualified={data.authedUser.disqualified}
              meetingBookedDetails={meetingBookedDetails}
              pendingAction={pendingAction}
              hasCadence={hasCadence}
              onToggleTracking={mutations.setTracking}
              onToggleDisqualified={mutations.setDisqualified}
              onAddFieldValue={mutations.updateField}
              onDeleteFieldValue={mutations.deleteFieldValue}
              // Only offer "move phone to org" when the contact actually has an org linked.
              onAssignPhoneToOrg={contactOrg != null ? mutations.assignPhoneToOrg : undefined}
              contactOrgName={contactOrg?.primaryFields?.name?.data ?? null}
              onMakeFieldPrimary={mutations.makeFieldPrimary}
              onRemoveFieldPrimary={mutations.removeFieldPrimary}
              onVerifyEmail={mutations.verifyEmail}
              onMarkFirmNumber={mutations.markPhoneFirmNumber}
              onRefreshFromLinkedIn={mutations.refreshFromLinkedIn}
              onEnhanceContact={handleEnhance}
              onDeleteMeetingBooked={mutations.deleteMeetingBooked}
              onAgeOverride={mutations.setAgeOverride}
              onResetGender={mutations.resetGender}
              isRefreshing={mutations.isRefreshing}
              isEnhancing={mutations.isEnhancing}
              isTrackingPending={mutations.isTrackingPending}
              isDisqualifiedPending={mutations.isDisqualifiedPending}
              isAgeOverridePending={mutations.isAgeOverridePending}
              isResetGenderPending={mutations.isResetGenderPending}
              onFieldCopied={onFieldCopied}
              onFieldCopyFailed={onFieldCopyFailed}
              linkedInMappings={linkedInMappings}
              linkedinUnavailable={linkedinUnavailable}
              isLinkedInMappingsLoading={isLinkedInMappingsLoading}
              onDeleteLinkedInMapping={onDeleteLinkedInMapping}
              onVerifyLinkedInMapping={onVerifyLinkedInMapping}
              onCopyLinkedInMapping={onCopyLinkedInMapping}
              onAddLinkedInMapping={onAddLinkedInMapping}
              isLinkedInMappingDeleting={isLinkedInMappingDeleting}
              isLinkedInMappingVerifying={isLinkedInMappingVerifying}
              isLinkedInMappingAdding={isLinkedInMappingAdding}
              onCopyToSuperadminTeam={onCopyToSuperadminTeam}
              isCopyPending={isCopyPending}
              superadminName={superadminName}
              superadminTeamName={superadminTeamName}
              contactImages={contactImages}
              isContactImagesLoading={isContactImagesLoading}
              onDownloadRecentPosts={onDownloadRecentPosts}
              isDownloadingRecentPosts={isDownloadingRecentPosts}
              onDeleteContact={onDeleteContact}
              isDeletingContact={isDeletingContact}
              onConnectUnipile={onUnipileConnect}
              onViewAllCampaigns={onViewAllCampaigns}
              onViewRequiredActions={onViewRequiredActions}
              renderContactTags={renderContactTags}
              signals={signals}
              isSignalsLoading={isSignalsLoading}
              lastUnipileFetchAt={lastUnipileFetchAt}
              ignoredAspectKeys={ignoredAspectKeys}
              experiences={experiences}
              education={education}
              isHistoryLoading={isHistoryLoading}
              onAddExperience={onAddExperience}
              orgSearch={orgSearch}
              onDeleteExperience={onDeleteExperience}
            />
          </>
        )}

        {/* Respond Section */}
        {respondStep != null && onRespondSubmit && onRespondChange && (
          <CadenceStepSectionPure
            userTeam={{
              teamId: authTeamId,
              userId: authUserId,
              isPremium: authIsPremium ?? false,
              // Must be the REAL team defaults, not `false`. The send now honours the value this
              // composer submits, so hardcoding them here would silently override a team that has
              // the signature (or contact-Bcc) on by default.
              useEmailSignatureByDefault: actionSectionData?.userTeam.useEmailSignatureByDefault ?? false,
              useContactEmailAsBcc: actionSectionData?.userTeam.useContactEmailAsBcc ?? false,
            }}
            contact={{ id: contact?.id ?? '', primaryFields: contact?.primaryFields ?? {} }}
            contactUser={{
              liConnectedAt: data.authedUser?.liConnectedAt ?? null,
              emailCredentialId: data.authedUser?.emailCredentialId ?? null,
            }}
            step={respondStep}
            atVariables={atVariables}
            documents={respondDocuments}
            fromEmailOptions={[]}
            toEmailOptions={[]}
            previousEmailThread={respondPreviousEmailThread}
            aiChoices={respondAiChoices}
            aiIndex={respondAiIndex}
            aiCommand={respondAiCommand}
            aiLoading={respondAiLoading}
            stepsList={respondStepsList}
            allowEditStepType={false}
            allowSaveAsTemplate={false}
            allowEmailDetails={false}
            disableLikeRecentPosts
            texts={{ submit: 'Respond' }}
            isSubmitting={respondIsSubmitting}
            isAiGenerating={respondIsAiGenerating}
            onSubmit={onRespondSubmit}
            onCancel={onCancelRespond}
            onChange={onRespondChange}
            onGenerateAi={onRespondGenerateAi}
            onAiCommandChange={onRespondAiCommandChange}
            onAiIndexChange={onRespondAiIndexChange}
            onToast={onRespondToast}
          />
        )}
      </div>

      {resumePrompt != null && (
        <ResumeOrStartFreshPopup
          campaignName={campaignTemplates.find((t) => t.id === resumePrompt.templateId)?.name ?? 'this campaign'}
          summary={resumePrompt.summary}
          onResume={() => {
            setResumePrompt(null);
            void mutations.enrollInCampaign(resumePrompt.templateId, { resumePriorSteps: true });
          }}
          onStartFresh={() => {
            setResumePrompt(null);
            void mutations.enrollInCampaign(resumePrompt.templateId, { resumePriorSteps: false });
          }}
          onClose={() => setResumePrompt(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Skeleton Components
// ============================================================================
